// KI-Assistent im App-Rahmen (design-system §3.6): ab 1280 px rechts angedockt (420 px, in der Breite ziehbar,
// schiebt den Inhalt), darunter eine überlagernde Spalte, auf dem Telefon ein Blatt in voller Höhe. Das Panel
// selbst meldet ein Modul über sein Manifest an (assistantPanel) und zeichnet in jeder Lage seinen Kopf selbst
// (Titel, Verlauf, Neu, Schließen); ohne Anmeldung steht hier ein ruhiger Hinweis. Nichts im Produkt ist nur über
// den Assistenten erreichbar.
import { X } from 'lucide-react'
import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  type ComponentType,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'
import { useLocation } from 'react-router'
import { create } from 'zustand'
import { useWebModules } from '../api/context'
import { SectionErrorBoundary } from '../api/pages/states'
import { hasPermission, passesGate } from '../api/permissions'
import { useSession } from '../api/session'
import { cn } from '../lib/cn'
import { useStoredState } from '../lib/storage'
import { useIsPhone } from '../lib/useMediaQuery'
import type { AssistantPanelProps } from '../module'
import { shell, useShellStore } from '../stores/shell'
import { Button } from '../ui/Button'
import { LayerProvider, useLayer, Z } from '../ui/context'
import { IconButton } from '../ui/IconButton'
import { Kbd } from '../ui/Kbd'
import { Logo } from '../ui/Logo'
import { Skeleton } from '../ui/Skeleton'

// ---- Verbindung zum Panel ---------------------------------------------------------------------------------
// Das Panel gehört einem Modul und kennt die Hülle nicht. Was es ihr mitzuteilen hat, schickt es als Ereignis
// am Fenster: „Befehlspalette öffnen“ und ob der Assistent zur Verfügung steht (die Schaltfläche in der Topbar
// bleibt sichtbar, wird aber gedämpft – design-system §3.6).

/** Name des Ereignisses; derselbe Wert steht in modules/assistant/web/AssistantPanel.tsx (SHELL_EVENT). */
export const ASSISTANT_SHELL_EVENT = 'merxo:assistant'
const ASSISTANT_PERMISSION = 'assistant.chat.use'

interface AvailabilityState {
  /** null = noch nicht bekannt (das Panel war noch nicht offen) */
  available: boolean | null
}
const useAvailabilityStore = create<AvailabilityState>(() => ({ available: null }))

/**
 * Steht der Assistent dieser Person zur Verfügung? false, sobald es feststeht (Recht fehlt, nicht eingerichtet,
 * Kontingent erschöpft) – dann dämpfen Topbar und Kopfzeile ihre Schaltfläche. Unbekannt gilt als verfügbar.
 */
export function useAssistantUnavailable(): boolean {
  const session = useSession()
  const modules = useWebModules()
  const reported = useAvailabilityStore((state) => state.available)
  if (!modules.some((module) => module.assistantPanel)) return true
  if (reported !== null) return !reported
  return !hasPermission(session, ASSISTANT_PERMISSION, 'any')
}

function useShellBridge(): void {
  const identity = useSession()?.user.id ?? ''
  useEffect(() => {
    // andere Person, anderer Stand
    useAvailabilityStore.setState({ available: null })
    const onMessage = (event: Event): void => {
      const detail = (event as CustomEvent<unknown>).detail as { type?: unknown; available?: unknown; query?: unknown } | null
      if (!detail || typeof detail !== 'object') return
      if (detail.type === 'open-palette') shell.openPalette(typeof detail.query === 'string' ? detail.query : '')
      if (detail.type === 'state')
        useAvailabilityStore.setState({ available: typeof detail.available === 'boolean' ? detail.available : null })
    }
    window.addEventListener(ASSISTANT_SHELL_EVENT, onMessage)
    return () => window.removeEventListener(ASSISTANT_SHELL_EVENT, onMessage)
  }, [identity])
}

// ---- Platzhalter ohne Modul ---------------------------------------------------------------------------------

function Placeholder({ onClose, initialMessage }: AssistantPanelProps) {
  return (
    <div className="flex min-h-0 flex-1 flex-col" data-testid="assistant-placeholder">
      <header className="flex h-(--topbar-h) shrink-0 items-center gap-2 border-b border-line pr-2 pl-4">
        <Logo variant="bare" size={16} title={null} />
        <h2 className="flex-1 text-dense font-semibold text-fg">KI-Assistent</h2>
        <IconButton icon={X} label="KI-Assistent schließen" size="sm" hotkey="mod+j" onClick={onClose} />
      </header>
      <div className="flex flex-1 flex-col justify-center gap-3 px-6 py-8">
        <span className="flex size-10 items-center justify-center rounded-md bg-sunken text-fg-muted">
          <Logo variant="bare" size={20} title={null} />
        </span>
        <p className="text-body font-medium text-fg">Der KI-Assistent ist hier noch nicht eingerichtet.</p>
        <p className="text-dense text-fg-muted">
          Sobald er eingerichtet ist, arbeitet er an dieser Stelle – mit genau den Rechten, die Sie am gewählten
          Standort haben, und mit Vorschau vor jeder Änderung. Bis dahin erreichen Sie alles über das Menü und die
          Befehlspalette.
        </p>
        {initialMessage && (
          <p className="rounded-md border border-line bg-raised px-3 py-2 text-dense text-fg-muted">
            Ihre Frage: <span className="text-fg">„{initialMessage}“</span>
          </p>
        )}
        <div>
          <Button size="sm" onClick={() => shell.openPalette(initialMessage ?? '')}>
            <span className="flex items-center gap-2">
              Befehlspalette öffnen <Kbd hotkey="mod+k" variant="plain" className="touch:hidden" />
            </span>
          </Button>
        </div>
      </div>
    </div>
  )
}

function useAssistantPanel(): ComponentType<AssistantPanelProps> {
  const modules = useWebModules()
  const session = useSession()
  const slot = modules.find(
    (module) => module.assistantPanel && passesGate(session, module.assistantPanel, 'any'),
  )?.assistantPanel
  return useMemo(() => (slot ? lazy(slot.lazy) : Placeholder), [slot])
}

// ---- überlagernd: Spalte rechts (ab 768 px) bzw. Blatt in voller Höhe (Telefon) ---------------------------------

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'

/**
 * Hält das Blatt genau im sichtbaren Bereich: Geht am Telefon die Tastatur auf, schrumpft der sichtbare Bereich
 * (visualViewport) – Eingabe und die Schaltflächen einer wartenden Vorschau bleiben so über der Tastatur.
 */
function useVisibleFrame(ref: { current: HTMLElement | null }, active: boolean): void {
  useEffect(() => {
    const viewport = typeof window !== 'undefined' ? window.visualViewport : null
    const node = ref.current
    if (!active || !viewport || !node) return
    const apply = (): void => {
      node.style.height = `${viewport.height}px`
      node.style.top = `${viewport.offsetTop}px`
    }
    apply()
    viewport.addEventListener('resize', apply)
    viewport.addEventListener('scroll', apply)
    return () => {
      viewport.removeEventListener('resize', apply)
      viewport.removeEventListener('scroll', apply)
      node.style.height = ''
      node.style.top = ''
    }
  }, [ref, active])
}

function AssistantSheet({ open, onClose, children }: { open: boolean; onClose: () => void; children: ReactNode }) {
  const phone = useIsPhone()
  const zIndex = useLayer(Z.drawer)
  const sheetRef = useRef<HTMLDivElement | null>(null)
  const anchorRef = useRef<HTMLSpanElement | null>(null)
  useVisibleFrame(sheetRef, open && phone)

  // Hintergrund stilllegen, Fokus ins Blatt holen und beim Schließen dorthin zurückgeben, wo er war
  useEffect(() => {
    if (!open) return
    const background = anchorRef.current?.parentElement ?? null
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null
    background?.setAttribute('inert', '')
    const timer = setTimeout(() => {
      const sheet = sheetRef.current
      if (sheet && !sheet.contains(document.activeElement)) sheet.focus({ preventScroll: true })
    }, 0)
    return () => {
      clearTimeout(timer)
      background?.removeAttribute('inert')
      if (previous?.isConnected) previous.focus({ preventScroll: true })
    }
  }, [open])

  const onKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>): void => {
    const sheet = sheetRef.current
    // Ebenen, die aus dem Blatt heraus geöffnet wurden (Menü, Dialog), liegen außerhalb und behandeln ihre Tasten selbst
    if (!sheet || !(event.target instanceof Node) || !sheet.contains(event.target)) return
    if (event.key === 'Escape' && !event.defaultPrevented) {
      event.stopPropagation()
      onClose()
      return
    }
    if (event.key !== 'Tab') return
    const items = [...sheet.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((item) => item.offsetParent !== null)
    const first = items[0]
    const last = items[items.length - 1]
    if (!first || !last) return
    if (event.shiftKey && (document.activeElement === first || document.activeElement === sheet)) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  }

  return (
    <>
      <span ref={anchorRef} hidden />
      {open &&
        createPortal(
          <div className="fixed inset-0" style={{ zIndex }} data-print="hide">
            <div className="absolute inset-0 animate-fade-in bg-scrim" onClick={onClose} aria-hidden />
            <div
              ref={sheetRef}
              role="dialog"
              aria-modal="true"
              aria-label="KI-Assistent"
              tabIndex={-1}
              data-testid="assistant-drawer"
              data-ui="drawer"
              onKeyDown={onKeyDown}
              onClick={(event) => {
                // Ein Verweis im Blatt führt auf eine Seite, die das Blatt verdeckt (auch wenn es die offene Seite
                // ist): zumachen, damit sie zu sehen ist. Neue Registerkarte (⌘/Strg/Mitte) lässt es offen.
                const link = event.target instanceof Element ? event.target.closest('a[href]') : null
                if (link && !event.metaKey && !event.ctrlKey && !event.shiftKey && link.getAttribute('target') !== '_blank')
                  onClose()
              }}
              className={cn(
                'absolute flex flex-col bg-surface text-fg shadow-lg outline-none',
                // Telefon: ganzes Blatt · ab 768 px: Spalte rechts in der Breite des angedockten Panels
                'inset-x-0 top-0 h-dvh animate-sheet-up motion-reduce:animate-none',
                'md:inset-y-0 md:right-0 md:left-auto md:h-full md:w-(--assistant-w) md:max-w-full md:animate-slide-in-end md:border-l md:border-line',
              )}
            >
              <LayerProvider z={zIndex}>{children}</LayerProvider>
            </div>
          </div>,
          document.body,
        )}
    </>
  )
}

// ---- angedockt: Breite per Griff 360–560 px ---------------------------------------------------------------------

const WIDTH_KEY = 'shell.assistantWidth'
const MIN_WIDTH = 360
const MAX_WIDTH = 560
const DEFAULT_WIDTH = 420
const clampWidth = (value: number): number => Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, Math.round(value)))

function ResizeHandle({ width, onChange }: { width: number; onChange: (width: number) => void }) {
  const drag = useRef<{ startX: number; startWidth: number } | null>(null)
  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>): void => {
    drag.current = { startX: event.clientX, startWidth: width }
    event.currentTarget.setPointerCapture(event.pointerId)
    event.preventDefault()
  }
  const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>): void => {
    if (!drag.current) return
    // der Griff sitzt links am Panel: nach links ziehen macht es breiter
    onChange(clampWidth(drag.current.startWidth + (drag.current.startX - event.clientX)))
  }
  const end = (): void => {
    drag.current = null
  }
  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label="Breite des KI-Assistenten"
      aria-valuemin={MIN_WIDTH}
      aria-valuemax={MAX_WIDTH}
      aria-valuenow={width}
      tabIndex={0}
      data-testid="assistant-resize"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={end}
      onPointerCancel={end}
      onDoubleClick={() => onChange(DEFAULT_WIDTH)}
      onKeyDown={(event) => {
        if (event.key === 'ArrowLeft') onChange(clampWidth(width + 16))
        else if (event.key === 'ArrowRight') onChange(clampWidth(width - 16))
        else if (event.key === 'Home') onChange(DEFAULT_WIDTH)
        else return
        event.preventDefault()
      }}
      className="group absolute inset-y-0 -left-1 z-(--z-sticky) w-2 cursor-col-resize touch-none outline-none"
    >
      <span className="absolute inset-y-0 left-1 w-px bg-transparent transition-colors duration-(--duration-fast) group-hover:bg-line-strong group-focus-visible:bg-accent group-active:bg-accent" />
    </div>
  )
}

/** Der Assistent als Spalte (docked) oder überlagernd. Steht einmal im App-Rahmen. */
export function AssistantDock({ docked }: { docked: boolean }) {
  const open = useShellStore((state) => state.assistantOpen)
  const seed = useShellStore((state) => state.assistantSeed)
  const Panel = useAssistantPanel()
  const [storedWidth, setStoredWidth] = useStoredState<number>(WIDTH_KEY, DEFAULT_WIDTH)
  const width = clampWidth(Number.isFinite(storedWidth) ? storedWidth : DEFAULT_WIDTH)
  useShellBridge()
  const close = useCallback(() => shell.setAssistant(false), [])

  // Überlagernd verdeckt das Panel die Seite: Wer aus ihm heraus einen Datensatz öffnet (Tabelle, Ergebnis-Karte),
  // will ihn sehen – das Blatt geht zu, die Unterhaltung bleibt und steht beim nächsten Öffnen wieder da.
  // Angedockt bleibt das Panel beim Navigieren offen.
  const { pathname } = useLocation()
  const lastPath = useRef(pathname)
  useEffect(() => {
    if (lastPath.current === pathname) return
    lastPath.current = pathname
    if (!docked && useShellStore.getState().assistantOpen) shell.setAssistant(false)
  }, [pathname, docked])

  const panel = (
    <SectionErrorBoundary label="Der KI-Assistent" size="md">
      <Suspense
        fallback={
          <div className="flex-1 p-6">
            <Skeleton variant="text" lines={4} />
          </div>
        }
      >
        <Panel
          docked={docked}
          onClose={close}
          initialMessage={seed}
          onInitialMessageConsumed={shell.clearAssistantSeed}
        />
      </Suspense>
    </SectionErrorBoundary>
  )
  if (docked) {
    if (!open) return null
    return (
      <aside
        aria-label="KI-Assistent"
        data-testid="assistant-dock"
        className="relative flex shrink-0 flex-col border-l border-line bg-surface"
        style={{ width }}
        data-print="hide"
      >
        <ResizeHandle width={width} onChange={setStoredWidth} />
        {panel}
      </aside>
    )
  }
  return (
    <AssistantSheet open={open} onClose={close}>
      {panel}
    </AssistantSheet>
  )
}
